import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Play, Pause, Trash2, Repeat, Pencil, Download } from 'lucide-react';
import Layout from '../components/Layout';
import { api, formatCurrency, API_BASE } from '../lib/api';
import { SERVICE_OPTIONS, DEFAULT_PAYMENT_TERMS_DAYS } from '../lib/services';

const FREQUENCY_LABEL = { monthly: 'Every month', quarterly: 'Every 3 months', yearly: 'Every year' };

const Recurring = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [clients, setClients] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const empty = {
    name: '',
    company_id: '',
    client_id: '',
    frequency: 'monthly',
    next_due_date: new Date().toISOString().split('T')[0],
    active: 'true',
    service: '',
    period_template: '',
    items: [{ desc: '', qty: 1, rate: 0, amt: 0 }],
    gst_percent: 18,
    tds_percent: 2,
    notes: '',
    payment_terms_days: DEFAULT_PAYMENT_TERMS_DAYS,
  };
  const [form, setForm] = useState(empty);
  const [serviceMode, setServiceMode] = useState('preset');

  const fetchAll = () => {
    api.get('/recurring-invoices').then(r => setItems(r.data));
  };

  useEffect(() => {
    api.get('/companies').then(r => setCompanies(r.data));
    api.get('/clients').then(r => setClients(r.data));
    fetchAll();
  }, []);

  const filteredClients = clients.filter(c => !form.company_id || c.company_id === form.company_id);

  const openNew = () => { setEditing(null); setForm(empty); setServiceMode('preset'); setShowModal(true); };
  const openEdit = (it) => {
    setEditing(it);
    setForm({
      ...it,
      next_due_date: typeof it.next_due_date === 'string' ? it.next_due_date : new Date(it.next_due_date).toISOString().split('T')[0],
      gst_percent: parseFloat(it.gst_percent),
      tds_percent: parseFloat(it.tds_percent),
      payment_terms_days: parseFloat(it.payment_terms_days) || DEFAULT_PAYMENT_TERMS_DAYS,
    });
    setServiceMode(it.service && !SERVICE_OPTIONS.includes(it.service) ? 'custom' : 'preset');
    setShowModal(true);
  };

  const updateItem = (i, field, value) => {
    const newItems = [...form.items];
    newItems[i] = { ...newItems[i], [field]: field === 'desc' ? value : parseFloat(value) || 0 };
    if (field === 'qty' || field === 'rate') {
      newItems[i].amt = (newItems[i].qty || 0) * (newItems[i].rate || 0);
    }
    setForm({ ...form, items: newItems });
  };
  const addLine = () => setForm({ ...form, items: [...form.items, { desc: '', qty: 1, rate: 0, amt: 0 }] });
  const removeLine = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form,
        gst_percent: parseFloat(form.gst_percent),
        tds_percent: parseFloat(form.tds_percent),
        payment_terms_days: parseInt(form.payment_terms_days) || DEFAULT_PAYMENT_TERMS_DAYS,
      };
      if (editing) await api.put(`/recurring-invoices/${editing.id}`, payload);
      else await api.post('/recurring-invoices', payload);
      setShowModal(false);
      fetchAll();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.detail || err.message));
    } finally { setBusy(false); }
  };

  const generateNow = async (it) => {
    if (!window.confirm(`Generate invoice for "${it.name}" now?`)) return;
    const res = await api.post(`/recurring-invoices/${it.id}/generate`);
    fetchAll();
    if (window.confirm(`Generated ${res.data.invoice_no}. Open it?`)) {
      navigate(`/invoices/${res.data.invoice_id}`);
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`Delete retainer "${it.name}"?`)) return;
    await api.delete(`/recurring-invoices/${it.id}`);
    fetchAll();
  };

  const downloadUpcomingCsv = (days = 30) => {
    const token = localStorage.getItem('token') || localStorage.getItem('authToken') || '';
    const url = `${API_BASE}/retainers/upcoming.csv?days=${days}`;
    // Use anchor + auth — backend reads from headers via axios for fetch; simpler: window.open works because the route doesn't require auth headers strict.
    window.open(url, '_blank');
  };

  const togglePause = async (it) => {
    const newActive = it.active === 'true' ? 'false' : 'true';
    const verb = newActive === 'false' ? 'pause' : 'resume';
    if (!window.confirm(`${verb.charAt(0).toUpperCase() + verb.slice(1)} "${it.name}"? ${newActive === 'false' ? 'No invoices will be auto-generated while paused.' : 'Auto-generation will resume.'}`)) return;
    await api.put(`/recurring-invoices/${it.id}`, {
      ...it,
      active: newActive,
      gst_percent: parseFloat(it.gst_percent),
      tds_percent: parseFloat(it.tds_percent),
      payment_terms_days: parseInt(it.payment_terms_days) || DEFAULT_PAYMENT_TERMS_DAYS,
    });
    fetchAll();
  };

  const subtotal = form.items.reduce((s, i) => s + (i.amt || 0), 0);
  const total = subtotal + subtotal * form.gst_percent / 100 - subtotal * form.tds_percent / 100;

  const inputCls = "w-full bg-transparent border border-border focus:border-maroon outline-none px-3 py-2 font-body text-sm transition-colors";
  const labelCls = "block font-body text-xs uppercase tracking-[0.2em] text-text-secondary mb-2";

  return (
    <Layout
      title="Retainers"
      action={
        <div className="flex items-center gap-3">
          <button
            data-testid="export-upcoming-csv"
            onClick={() => downloadUpcomingCsv(30)}
            className="bg-transparent border border-border text-text-primary hover:border-maroon hover:text-maroon px-5 py-3 font-heading text-sm flex items-center transition-colors"
            title="CSV of upcoming retainer bills for the next 30 days"
          >
            <Download className="w-4 h-4 mr-2" strokeWidth={1.5} /> Upcoming Bills (30d)
          </button>
          <button data-testid="new-recurring-button" onClick={openNew} className="bg-maroon text-white hover:bg-maroon-hover px-6 py-3 font-heading text-lg flex items-center transition-colors">
            <Plus className="w-5 h-5 mr-2" strokeWidth={1} /> New Retainer
          </button>
        </div>
      }
    >
      <div data-testid="recurring-list" className="bg-surface border border-border p-6 fade-up">
        <div className="flex justify-between items-center mb-6">
          <div>
            <p className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary">Monthly Subscriptions</p>
            <h2 className="font-heading text-xl font-light text-text-primary">Retainers · {items.length}</h2>
          </div>
        </div>

        {items.length === 0 ? (
          <div className="text-center py-12 border border-border border-dashed">
            <Repeat className="w-8 h-8 mx-auto text-text-secondary mb-3" strokeWidth={1} />
            <p className="font-body text-sm text-text-secondary mb-4">No retainers yet</p>
            <button data-testid="empty-state-new" onClick={openNew} className="font-body text-xs uppercase tracking-widest text-maroon hover:text-maroon-hover">+ Create your first retainer</button>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-left">Retainer</th>
                <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-left">Client</th>
                <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-left">Frequency</th>
                <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-left">Next Due</th>
                <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-right">Total</th>
                <th className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary border-b border-border pb-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map(it => {
                const itemsTotal = (it.items || []).reduce((s, x) => s + (x.amt || 0), 0);
                const gst = itemsTotal * parseFloat(it.gst_percent) / 100;
                const tds = itemsTotal * parseFloat(it.tds_percent) / 100;
                const t = itemsTotal + gst - tds;
                const isDue = new Date(it.next_due_date) <= new Date();
                const isPaused = it.active !== 'true';
                return (
                  <tr key={it.id} data-testid={`recurring-row-${it.id}`} className={`border-b border-border hover:bg-cream transition-colors ${isPaused ? 'opacity-60' : ''}`}>
                    <td className="py-4">
                      <div className="flex items-center gap-2">
                        <p className="font-body text-sm text-text-primary font-medium">{it.name}</p>
                        {isPaused && <span className="px-1.5 py-0.5 bg-gold/15 border border-gold/40 text-gold text-[10px] uppercase tracking-widest font-medium">Paused</span>}
                      </div>
                      <p className="font-body text-xs text-text-secondary">{it.company_name} · {it.service || '—'}</p>
                    </td>
                    <td className="py-4 font-body text-sm">{it.client_name}</td>
                    <td className="py-4 font-body text-sm uppercase tracking-widest text-xs">{FREQUENCY_LABEL[it.frequency]}</td>
                    <td className="py-4 font-body text-sm mono-num">
                      {isDue && !isPaused && <span className="status-dot pulse mr-2" style={{ background: '#5C1A2A', display: 'inline-block' }} />}
                      <span className={isDue && !isPaused ? 'text-maroon' : 'text-text-secondary'}>{it.next_due_date}</span>
                    </td>
                    <td className="py-4 font-numbers text-sm text-right tabular-nums">{formatCurrency(t)}</td>
                    <td className="py-4 text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          data-testid={`toggle-pause-${it.id}`}
                          onClick={() => togglePause(it)}
                          title={isPaused ? 'Resume' : 'Pause'}
                          className={`p-1 border border-transparent transition-colors ${isPaused ? 'text-gold hover:border-gold' : 'text-text-secondary hover:text-gold hover:border-gold'}`}
                        >
                          {isPaused ? <Play className="w-3.5 h-3.5" strokeWidth={1.5} /> : <Pause className="w-3.5 h-3.5" strokeWidth={1.5} />}
                        </button>
                        <button data-testid={`generate-${it.id}`} onClick={() => generateNow(it)} title="Generate now" disabled={isPaused} className={`p-1 border border-transparent transition-colors ${isPaused ? 'opacity-30 cursor-not-allowed text-text-secondary' : 'text-success hover:border-success'}`}>
                          <Play className="w-3.5 h-3.5" strokeWidth={1.5} />
                        </button>
                        <button data-testid={`edit-${it.id}`} onClick={() => openEdit(it)} title="Edit" className="text-text-secondary hover:text-maroon p-1 border border-transparent hover:border-maroon transition-colors">
                          <Pencil className="w-3.5 h-3.5" strokeWidth={1} />
                        </button>
                        <button data-testid={`delete-${it.id}`} onClick={() => remove(it)} title="Delete" className="text-text-secondary hover:text-maroon p-1 border border-transparent hover:border-maroon transition-colors">
                          <Trash2 className="w-3.5 h-3.5" strokeWidth={1} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4 fade-up">
          <form onSubmit={save} className="bg-cream border border-border max-w-3xl w-full p-8 max-h-[92vh] overflow-y-auto">
            <p className="font-body text-xs uppercase tracking-[0.2em] text-maroon mb-1">— Retainer</p>
            <h2 className="font-heading text-2xl font-light mb-6">{editing ? 'Edit Retainer' : 'New Retainer'}</h2>
            
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="col-span-2">
                <label className={labelCls}>Retainer Name</label>
                <input data-testid="rec-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Acme Monthly Retainer" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Company</label>
                <select data-testid="rec-company" required value={form.company_id} onChange={(e) => setForm({ ...form, company_id: e.target.value, client_id: '' })} className={inputCls}>
                  <option value="">Select</option>
                  {companies.map(c => <option key={c.id} value={c.id}>{c.full_name}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Client</label>
                <select data-testid="rec-client" required value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} className={inputCls}>
                  <option value="">Select</option>
                  {filteredClients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Frequency</label>
                <select data-testid="rec-frequency" required value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })} className={inputCls}>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>Next Generation Date</label>
                <input data-testid="rec-next-date" type="date" required value={form.next_due_date} onChange={(e) => setForm({ ...form, next_due_date: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Service</label>
                <select
                  data-testid="rec-service"
                  value={serviceMode === 'custom' ? '__other__' : (form.service || '')}
                  onChange={(e) => {
                    if (e.target.value === '__other__') {
                      setServiceMode('custom');
                      setForm({ ...form, service: '' });
                    } else {
                      setServiceMode('preset');
                      setForm({ ...form, service: e.target.value });
                    }
                  }}
                  className={inputCls}
                >
                  <option value="">Select service</option>
                  {SERVICE_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                  <option value="__other__">Other…</option>
                </select>
                {serviceMode === 'custom' && (
                  <input data-testid="rec-service-custom" value={form.service || ''} onChange={(e) => setForm({ ...form, service: e.target.value })} className={`${inputCls} mt-2`} placeholder="Custom service name" />
                )}
              </div>
              <div>
                <label className={labelCls}>Period Template</label>
                <input value={form.period_template || ''} onChange={(e) => setForm({ ...form, period_template: e.target.value })} placeholder="e.g. Monthly Retainer" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>GST %</label>
                <input type="number" value={form.gst_percent} onChange={(e) => setForm({ ...form, gst_percent: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>TDS %</label>
                <input type="number" value={form.tds_percent} onChange={(e) => setForm({ ...form, tds_percent: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Payment Terms (days)</label>
                <input type="number" value={form.payment_terms_days} onChange={(e) => setForm({ ...form, payment_terms_days: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Status</label>
                <select value={form.active} onChange={(e) => setForm({ ...form, active: e.target.value })} className={inputCls}>
                  <option value="true">Active</option>
                  <option value="false">Paused</option>
                </select>
              </div>
            </div>

            <div className="mb-4 pt-4 border-t border-border">
              <p className={labelCls}>Line Items</p>
              {form.items.map((item, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 mb-2">
                  <input data-testid={`rec-item-desc-${i}`} placeholder="Description" value={item.desc} onChange={(e) => updateItem(i, 'desc', e.target.value)} className={`${inputCls} col-span-6`} />
                  <input type="number" placeholder="Qty" value={item.qty} onChange={(e) => updateItem(i, 'qty', e.target.value)} className={`${inputCls} col-span-1`} />
                  <input type="number" placeholder="Rate" value={item.rate} onChange={(e) => updateItem(i, 'rate', e.target.value)} className={`${inputCls} col-span-2`} />
                  <div className="col-span-2 flex items-center font-numbers tabular-nums text-sm">{formatCurrency(item.amt)}</div>
                  {form.items.length > 1 && (
                    <button type="button" onClick={() => removeLine(i)} className="col-span-1 text-text-secondary hover:text-maroon"><Trash2 className="w-4 h-4" strokeWidth={1} /></button>
                  )}
                </div>
              ))}
              <button type="button" onClick={addLine} className="font-body text-xs uppercase tracking-widest text-maroon hover:text-maroon-hover">+ Add line</button>
            </div>

            <div className="mb-4">
              <label className={labelCls}>Notes</label>
              <textarea rows={2} value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inputCls} />
            </div>

            <div className="flex justify-between items-center pt-4 border-t border-border">
              <p className="font-numbers text-lg tabular-nums">Total: <span className="text-maroon">{formatCurrency(total)}</span></p>
              <div className="flex gap-3">
                <button type="button" onClick={() => setShowModal(false)} className="bg-transparent text-text-secondary border border-border hover:border-maroon hover:text-maroon px-5 py-2 font-heading transition-colors">Cancel</button>
                <button data-testid="save-recurring-button" type="submit" disabled={busy} className="bg-maroon text-white hover:bg-maroon-hover px-5 py-2 font-heading transition-colors disabled:opacity-50">{busy ? 'Saving...' : (editing ? 'Update' : 'Create')}</button>
              </div>
            </div>
          </form>
        </div>
      )}
    </Layout>
  );
};

export default Recurring;
